import { useEffect } from 'react';

/**
 * Applies a fade-in + slide-up reveal to every element with the `.reveal`
 * class as it enters the viewport. Uses IntersectionObserver so it's cheap
 * and only observes visible elements once.
 * The site.css rules for `.reveal` and `.reveal.is-visible` do the animation.
 */
export function useReveal() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('IntersectionObserver' in window)) {
      // Fallback: just show everything.
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    const observe = () => {
      document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
    };
    observe();
    // Re-scan after route/content updates (light polling for the first few seconds).
    const t1 = setTimeout(observe, 400);
    const t2 = setTimeout(observe, 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      io.disconnect();
    };
  }, []);
}
